/**
 * WASSER E-commerce Platform - Shared LoadingSpinner Component
 *
 * Унифицированный компонент индикатора загрузки.
 * Используется в Client и Admin приложениях.
 *
 * @example
 * // Базовое использование
 * <LoadingSpinner />
 *
 * // С кастомным сообщением
 * <LoadingSpinner message="Загрузка товаров..." />
 *
 * // Разные размеры
 * <LoadingSpinner size="small" />
 * <LoadingSpinner size="large" />
 *
 * // Полноэкранный режим
 * <LoadingSpinner fullScreen />
 *
 * // Оверлей поверх контента
 * <LoadingSpinner overlay />
 */
import React from 'react';
import { Box, CircularProgress, Typography, Fade } from '@mui/material';

/**
 * Размеры спиннера
 */
const SIZES = {
  small: 24,
  medium: 40,
  large: 56,
};

/**
 * LoadingSpinner Component
 *
 * @param {Object} props
 * @param {string} [props.message='Загрузка...'] - Текст под спиннером
 * @param {string|number} [props.size='medium'] - Размер: 'small' | 'medium' | 'large' | number
 * @param {string} [props.color='primary'] - Цвет: 'primary' | 'secondary' | 'inherit'
 * @param {boolean} [props.fullScreen=false] - Занимает весь экран
 * @param {boolean} [props.overlay=false] - Показывать как оверлей с backdrop
 * @param {number} [props.minHeight=200] - Минимальная высота контейнера
 * @param {boolean} [props.showMessage=true] - Показывать сообщение
 * @param {Object} [props.sx] - Дополнительные стили контейнера
 */
const LoadingSpinner = ({
  message = 'Загрузка...',
  size = 'medium',
  color = 'primary',
  fullScreen = false,
  overlay = false,
  minHeight = 200,
  showMessage = true,
  sx = {},
}) => {
  // Resolve size value
  const spinnerSize = typeof size === 'number' ? size : SIZES[size] || SIZES.medium;

  // Base container styles
  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: fullScreen ? '100vh' : minHeight,
    py: 4,
    ...sx,
  };

  // Full screen styles
  if (fullScreen) {
    Object.assign(containerStyles, {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: 'background.default',
      zIndex: 9999,
    });
  }

  // Overlay styles
  if (overlay) {
    Object.assign(containerStyles, {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(2px)',
      zIndex: 1000,
    });
  }

  return (
    <Fade in timeout={300}>
      <Box sx={containerStyles}>
        <CircularProgress
          size={spinnerSize}
          color={color}
          sx={{ mb: showMessage ? 2 : 0 }}
        />
        {showMessage && message && (
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>
    </Fade>
  );
};

/**
 * Skeleton варианты для конкретных случаев
 */

/**
 * Спиннер для кнопок (inline, без сообщения)
 */
export const ButtonSpinner = ({ size = 20, color = 'inherit' }) => (
  <CircularProgress size={size} color={color} />
);

/**
 * Спиннер для карточек
 */
export const CardSpinner = ({ message = 'Загрузка...' }) => (
  <LoadingSpinner size="small" message={message} minHeight={150} />
);

/**
 * Полноэкранный спиннер
 */
export const FullScreenSpinner = ({ message = 'Загрузка...' }) => (
  <LoadingSpinner fullScreen message={message} size="large" />
);

/**
 * Оверлей спиннер
 */
export const OverlaySpinner = ({ message = 'Сохранение...' }) => (
  <LoadingSpinner overlay message={message} />
);

export default LoadingSpinner;
